import { Controller } from 'stimulus'
import { useIntersection } from 'stimulus-use'

const LAZYLOAD_STATUS = {
  UNINITIALIZED: 'uninitialized',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
}

export default class LazyLoad extends Controller {
  static values = {
    allowAspectRatio: Number,
    allowDomains: String,
    src: String,
    srcDefault: String,
    srcset: String,
    srcsetDefault: String,
  }

  connect() {
    useIntersection(this)

    this.loadStatus = LAZYLOAD_STATUS.UNINITIALIZED
  }

  /* Check `data-lazyload-allow-aspect-ratio-value` attribute */
  get isAspectRatioAllowed() {
    if (this.allowAspectRatioValue) {
      if (this.element.naturalWidth / this.element.naturalHeight !== this.allowAspectRatioValue) {
        return false
      }
    }
    return true
  }

  /* Check `data-lazyload-allow-domains-value` attribute that can only enable certain domains to load images from */
  get isDomainAllowed() {
    if (this.allowDomainsValue) {
      return this.allowDomainsValue.split(',').every((domain) => {
        if (this.srcValue?.indexOf(domain.trim()) === -1 || this.srcsetValue?.indexOf(domain.trim()) === -1) {
          return false
        }
        return true
      })
    }
    return true
  }

  loadingCompleted() {
    if (this.isAspectRatioAllowed) {
      this.loadStatus = LAZYLOAD_STATUS.LOADED
      this.element.classList.add('is-loaded')
    } else if (this.loadStatus !== 'failed') {
      this.loadingFailed()
    }
  }

  loadingFailed() {
    this.loadStatus = LAZYLOAD_STATUS.FAILED
    this.element.classList.add('is-failed-loading')
    // Load default image if default src is set
    if (
      (this.srcDefaultValue || this.srcsetDefaultValue) &&
      (this.element.src !== this.srcDefaultValue || this.element.srcset !== this.srcsetDefaultValue)
    ) {
      if (this.srcDefaultValue) {
        this.element.src = this.srcDefaultValue
      }
      if (this.srcsetDefaultValue) {
        this.element.srcset = this.srcsetDefaultValue
      }
    }
  }

  loadImage() {
    ;['src', 'srcset'].forEach((attribute) => {
      if (this[`${attribute}Value`]) {
        this.element.setAttribute(attribute, this[`${attribute}Value`])
      }
    })
    this.loadStatus = LAZYLOAD_STATUS.LOADING
  }

  bindEvents() {
    this.element.addEventListener('load', this.loadingCompleted.bind(this))
    this.element.addEventListener('error', this.loadingFailed.bind(this))
  }

  appear() {
    if (this.loadStatus === LAZYLOAD_STATUS.UNINITIALIZED && this.isDomainAllowed) {
      this.bindEvents()
      this.loadImage()
    } else if (!this.isDomainAllowed && this.loadStatus !== LAZYLOAD_STATUS.FAILED) {
      this.loadingFailed()
    }
  }
}
